import type {} from '@mui/lab/themeAugmentation';
import type {} from '@mui/x-date-pickers/themeAugmentation';

import { Components, Theme } from '@mui/material/styles';

import { DialogTransition } from '@/components/DialogTransition.comp.tsx';
import { themeColors } from '@/config/theme/themeColors.ts';

export const components: Components<Omit<Theme, 'components'>> = {
  MuiAvatar: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.f20Regular,
        backgroundColor: 'transparent',
        border: `1px solid ${theme.colors.stroke}`,
        color: theme.colors.grey,
        height: 88,
        width: 88,
      }),
    },
  },
  MuiButton: {
    defaultProps: {
      size: 'medium',
      variant: 'contained',
    },
    styleOverrides: {
      root: ({ ownerState, theme }) => ({
        ...theme.typography.f16Regular,
        border: '1px solid',
        borderRadius: '8px',
        textTransform: 'none',
        ...(ownerState.size === 'medium' && {
          padding: '4px 12px',
        }),
        ...(ownerState.size === 'small' && {
          padding: '2px 6px',
        }),
        ...(ownerState.variant === 'contained' && {
          '&:focus': {
            background: theme.colors.accentColor,
            borderColor: theme.colors.accentColorDark,
          },
          '&:hover': {
            background: theme.colors.accentColorDark,
          },
          '&.Mui-disabled': {
            background: theme.colors.greyLight,
            borderColor: theme.colors.greyLight,
          },
          background: theme.colors.accentColor,
          borderColor: theme.colors.accentColor,
          color: theme.colors.white,
          ...(ownerState.color === 'info' && {
            '&:hover': {
              backgroundColor: theme.colors.greyDark,
              borderColor: theme.colors.greyDark,
            },
            backgroundColor: theme.colors.grey,
            borderColor: theme.colors.grey,
          }),
          ...(ownerState.color === 'secondary' && {
            '&:hover': {
              backgroundColor: theme.colors.blueDark,
              borderColor: theme.colors.blueDark,
            },
            background: theme.colors.blue,
            borderColor: theme.colors.blue,
            color: theme.colors.white,
          }),
          ...(ownerState.color === 'success' && {
            '&:hover': {
              backgroundColor: theme.colors.greenDark,
              borderColor: theme.colors.greenDark,
            },
            background: theme.colors.green,
            borderColor: theme.colors.green,
            color: theme.colors.white,
          }),
          ...(ownerState.color === 'warning' && {
            '&:hover': {
              backgroundColor: theme.colors.orangeDark,
              borderColor: theme.colors.orangeDark,
            },
            background: theme.colors.orange,
            borderColor: theme.colors.orange,
            color: theme.colors.white,
          }),
          ...(ownerState.color === 'error' && {
            '&:hover': {
              backgroundColor: theme.colors.redDark,
              borderColor: theme.colors.redDark,
            },
            background: theme.colors.red,
            borderColor: theme.colors.red,
            color: theme.colors.white,
          }),
          ...(ownerState.color === 'neutral' && {
            '&:focus': {
              background: theme.colors.neutral,
              borderColor: theme.colors.navyDark,
            },
            '&:hover': {
              backgroundColor: theme.colors.navyDark,
              borderColor: theme.colors.navyDark,
            },
            background: theme.colors.neutral,
            borderColor: theme.colors.neutral,
            color: theme.colors.white,
          }),
        }),
        ...(ownerState.variant === 'outlined' && {
          '&:focus': {
            borderColor: theme.colors.accentColorDark,
            color: theme.colors.accentColorDark,
          },
          '&:hover': {
            borderColor: theme.colors.accentColorDark,
            color: theme.colors.accentColorDark,
          },
          '&.Mui-disabled': {
            borderColor: theme.colors.greyLight,
            color: theme.colors.greyLight,
          },
          borderColor: theme.colors.accentColor,
          color: theme.colors.accentColor,
          ...(ownerState.color === 'info' && {
            '&:focus': {
              borderColor: theme.colors.greenDark,
              color: theme.colors.greenDark,
            },
            '&:hover': {
              borderColor: theme.colors.greyDark,
            },
            borderColor: theme.colors.grey,
            color: theme.colors.grey,
          }),
          ...(ownerState.color === 'secondary' && {
            '&:focus': {
              borderColor: theme.colors.blueDark,
              color: theme.colors.blueDark,
            },
            '&:hover': {
              borderColor: theme.colors.blueDark,
            },
            borderColor: theme.colors.blue,
            color: theme.colors.blue,
          }),
          ...(ownerState.color === 'success' && {
            '&:focus': {
              borderColor: theme.colors.greenDark,
              color: theme.colors.greenDark,
            },
            '&:hover': {
              borderColor: theme.colors.greenDark,
            },
            borderColor: theme.colors.green,
            color: theme.colors.green,
          }),
          ...(ownerState.color === 'warning' && {
            '&:focus': {
              borderColor: theme.colors.orangeDark,
              color: theme.colors.orangeDark,
            },
            '&:hover': {
              borderColor: theme.colors.orangeDark,
            },
            borderColor: theme.colors.orange,
            color: theme.colors.orange,
          }),
          ...(ownerState.color === 'error' && {
            '&:focus': {
              borderColor: theme.colors.redDark,
              color: theme.colors.redDark,
            },
            '&:hover': {
              borderColor: theme.colors.redDark,
            },
            borderColor: theme.colors.red,
            color: theme.colors.red,
          }),
          ...(ownerState.color === 'neutral' && {
            '&:focus': {
              borderColor: theme.colors.navyDark,
              color: theme.colors.navyDark,
            },
            '&:hover': {
              backgroundColor: theme.colors.bg,
              borderColor: theme.colors.navyDark,
              color: theme.colors.navyDark,
            },
            backgroundColor: theme.colors.white,
            borderColor: theme.colors.neutral,
            color: theme.colors.neutral,
          }),
        }),
        ...(ownerState.variant === 'text' && {
          '&:focus': {
            color: theme.colors.accentColorDark,
          },
          '&:hover': {
            background: theme.colors.bg,
            color: theme.colors.accentColorDark,
          },
          '&.Mui-disabled': {
            color: theme.colors.greyLight,
          },
          borderColor: 'transparent',
          color: theme.colors.accentColor,
          ...(ownerState.color === 'neutral' && {
            '&:focus': {
              color: theme.colors.navyDark,
            },
            '&:hover': {
              background: theme.colors.stroke,
              color: theme.colors.navyDark,
            },
            color: theme.colors.neutral,
          }),
        }),
      }),
    },
  },
  MuiButtonBase: {
    defaultProps: {
      disableRipple: true,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        '&.MuiPickerDay-root': {
          '&:focus': {
            '&.Mui-selected': {
              backgroundColor: theme.colors.accentColor,
              color: theme.colors.white,
            },
          },
          '&:hover': {
            backgroundColor: theme.colors.accentColorLight,
            color: theme.colors.white,
          },
          '&.Mui-selected': {
            '&:hover': {
              backgroundColor: theme.colors.accentColorDark,
              color: theme.colors.white,
            },
            backgroundColor: theme.colors.accentColor,
            borderRadius: '500px',
            color: theme.colors.white,
          },
        },
        '&.MuiPickerDay-today': {
          border: 'none !important',
          borderBottom: `2px solid ${theme.colors.greyLight} !important`,
          borderRadius: 0,
        },
        '&.MuiSwitch-switchBase': {
          '&.Mui-checked + .MuiSwitch-track': {
            backgroundColor: themeColors.accentColorDark,
          },
        },
        '&.MuiTab-root': {
          padding: '0',
        },
        '&.MuiTabScrollButton-root': {
          '&.Mui-disabled': {
            color: theme.colors.white,
            opacity: 1,
          },
        },
      }),
    },
  },
  MuiCheckbox: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&.Mui-checked': {
          color: theme.colors.accentColor,
        },
        paddingBottom: 0,
        paddingTop: 0,
      }),
    },
  },
  MuiChip: {
    styleOverrides: {
      root: ({ ownerState, theme }) => ({
        ...(ownerState.color === 'default' && {
          '&.MuiChip-clickable:hover, &.Mui-focusVisible': {
            backgroundColor: theme.colors.accentColor10,
          },
          color: theme.colors.greyDarker,
          ...(ownerState.variant === 'outlined'
            ? {
                backgroundColor: theme.colors.white,
                borderColor: theme.colors.greyLight,
              }
            : {
                backgroundColor: theme.colors.stroke,
              }),
        }),
        ...(ownerState.color === 'primary' && {
          '&.MuiChip-clickable:hover, &.Mui-focusVisible': {
            backgroundColor: theme.colors.accentColor20,
          },
          backgroundColor: theme.colors.accentColor10,
          border: `1px solid ${theme.colors.accentColor}`,
          color: theme.colors.accentColorDark,
        }),
        ...(ownerState.color === 'info' && {
          backgroundColor: theme.colors.purple,
          color: theme.colors.purpleLight,
        }),
      }),
    },
  },
  MuiDialog: {
    defaultProps: {
      slots: {
        transition: DialogTransition,
      },
    },
    styleOverrides: {
      paper: {
        '& > div.MuiDialogContent-root': { paddingTop: '8px' },
        maxWidth: 960,
        width: 960,
      },
      root: {
        margin: 'auto',
        width: 960,
      },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        paddingBottom: 24,
        paddingRight: 24,
      },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: {
        paddingTop: '48px',
      },
    },
  },
  MuiDivider: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderColor: theme.colors.stroke,
        marginBottom: 16,
        marginTop: 16,
      }),
    },
  },
  MuiFormControlLabel: {
    styleOverrides: {
      asterisk: ({ theme }) => ({
        color: theme.colors.red,
      }),
      label: ({ theme }) => ({
        ...theme.typography.f12Regular,
        '&.Mui-focused': {
          color: theme.colors.greyDark,
        },
        color: theme.colors.greyDark,
        top: -4,
        transform: 'none',
      }),
    },
  },
  MuiInput: {
    styleOverrides: {
      input: ({ theme }) => ({
        '&::placeholder': {
          ...theme.typography.f14Regular,
          color: theme.colors.grey,
        },
        padding: 0,
      }),
      root: ({ theme }) => ({
        ...theme.typography.f14Regular,
        '&::before': {
          borderBottom: 'none',
        },
        '&:hover:not(.Mui-disabled, .Mui-error)': {
          '&::before': {
            borderBottom: 'none',
          },
        },
        '&:hover:not(.Mui-focused):not(.Mui-error)': {
          borderColor: theme.colors.greyLight,
        },
        '&.Mui-disabled': {
          '&::before': {
            borderBottom: 'none',
          },
        },
        border: `1px solid ${theme.colors.stroke}`,
        borderRadius: 4,
        padding: '12px 16px',
      }),
    },
  },
  MuiInputBase: {
    styleOverrides: {
      input: ({ theme }) => ({
        '&.MuiInput-input': {
          '&.Mui-disabled': {
            textFillColor: theme.colors.grey,
          },
        },
        '&.MuiOutlinedInput-input': {
          '&::placeholder': {
            ...theme.typography.f14Regular,
            color: theme.colors.grey,
          },
          '&.Mui-disabled': {
            textFillColor: theme.colors.grey,
          },
          '&.Mui-error': {
            borderColor: theme.colors.blue,
          },
          '&.Mui-focused': {
            borderColor: theme.colors.blue,
          },
          padding: 8,
        },
      }),
      root: ({ theme }) => ({
        '&:hover': {
          '& .MuiOutlinedInput-notchedOutline': {
            '&:hover': {
              border: 'none',
            },
            border: 'none',
          },
        },
        '&.MuiInput-root': {
          '&.Mui-disabled': {
            backgroundColor: theme.colors.stroke,
          },
          '&.Mui-error': {
            borderColor: theme.colors.red,
          },
          '&.Mui-focused': {
            borderColor: theme.colors.accentColor,
          },
          backgroundColor: theme.colors.white,
          padding: '8px 12px',
        },
        '&.MuiOutlinedInput-root': {
          '&.Mui-disabled': {
            backgroundColor: theme.colors.stroke,
          },
          '&.Mui-error': {
            borderColor: theme.colors.red,
          },
          minHeight: '38px',
        },
      }),
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      asterisk: ({ theme }) => ({
        color: theme.colors.red,
      }),
      root: ({ theme }) => ({
        ...theme.typography.f12Regular,
        '&.Mui-focused': {
          color: theme.colors.greyDark,
        },
        '&.MuiInputLabel-outlined': {
          '&.MuiInputLabel-shrink': {
            backgroundColor: theme.colors.white,
            padding: '0 4px',
            transform: 'translate(0px, -14px) scale(0.75)',
          },
        },
        '&.MuiInputLabel-standard': {
          '&.MuiInputLabel-shrink': {
            transform: 'none',
          },
          top: -4,
          transform: 'none',
        },
        color: theme.colors.greyDark,
      }),
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.f14Regular,
        '&.Mui-selected': {
          '&:hover': {
            backgroundColor: theme.colors.greenLight,
          },
          backgroundColor: theme.colors.greenLight,
        },
        '&.MuiMultiSectionDigitalClockSection-item': {
          '&:hover': {
            backgroundColor: theme.colors.accentColorLight,
            color: theme.colors.white,
          },
          '&.Mui-selected': {
            '&:hover': {
              backgroundColor: theme.colors.accentColorDark,
              color: theme.colors.white,
            },
            backgroundColor: theme.colors.accentColor,
            color: theme.colors.white,
          },
          borderRadius: '50px',
        },
      }),
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      notchedOutline: {
        border: 'none',
      },
      root: ({ theme }) => ({
        ...theme.typography.f12Regular,
        '&:hover:not(.Mui-focused)': {
          borderColor: theme.colors.greyLight,
        },
        '&.Mui-focused': {
          color: theme.colors.black,
        },
        border: `1px solid ${theme.colors.stroke}`,
        borderRadius: 4,
        color: theme.colors.black,
        transform: 'none',
      }),
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        '&.MuiAccordion-root': {
          '&::before': {
            height: 0,
          },
          '&:last-of-type, :first-of-type': {
            borderRadius: 0,
          },
          '&.Mui-expanded': {
            margin: 0,
          },
          borderRadius: 0,
        },
        borderRadius: 0,
      },
    },
  },
  MuiPickersInputBase: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.f14Regular,
        '&.Mui-disabled': {
          color: theme.colors.grey,
        },
        color: theme.colors.black,
      }),
      sectionsContainer: {
        width: 'auto',
      },
    },
  },
  MuiPickersOutlinedInput: {
    styleOverrides: {
      notchedOutline: {
        border: 'none',
      },
      root: ({ theme }) => ({
        '&:hover:not(.Mui-focused):not(.Mui-error)': {
          borderColor: theme.colors.greyLight,
        },
        '&.Mui-disabled': {
          backgroundColor: theme.colors.stroke,
        },
        '&.Mui-error': {
          borderColor: theme.colors.red,
        },
        '&.Mui-focused': {
          '& .MuiPickersOutlinedInput-notchedOutline': {
            borderWidth: 0,
          },
          borderColor: theme.colors.accentColor,
        },
        backgroundColor: theme.colors.white,
        border: `1px solid ${theme.colors.stroke}`,
        borderRadius: 4,
        minHeight: '38px',
        padding: '0 12px',
      }),
      sectionsContainer: {
        padding: '8px 0',
      },
    },
  },
  MuiRadio: {
    styleOverrides: {
      root: {
        '&.Mui-checked': {
          color: 'black',
        },
        color: 'black',
      },
    },
  },
  MuiStack: {
    defaultProps: {
      useFlexGap: true,
    },
  },
  MuiStepIcon: {
    styleOverrides: {
      root: {
        '&.Mui-active, &.Mui-completed': {
          color: themeColors.accentColor,
        },
      },
    },
  },
  MuiSvgIcon: {
    styleOverrides: {
      root: {
        fontSize: '20px',
      },
    },
  },
  MuiSwitch: {
    styleOverrides: {
      track: {
        backgroundColor: themeColors.accentColorLight,
      },
    },
  },
  MuiTab: {
    defaultProps: {
      sx: { width: '100%' },
    },
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.f14Regular,
        '&:hover': { backgroundColor: theme.colors.stroke40 },
        '&.Mui-selected': {
          '&:hover': { backgroundColor: theme.colors.stroke20 },
          color: theme.colors.accentColor,
        },
        backgroundColor: theme.colors.white,
        border: `1px solid ${theme.colors.stroke}`,
        color: theme.colors.greyDark,
      }),
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: '3px 8px',
        ...theme.typography.f14Regular,
        '&.MuiTableCell-body': {
          '&:first-of-type': {
            color: theme.colors.accentColor,
          },
        },
        '&.MuiTableCell-head': {
          color: theme.colors.grey,
        },
      }),
    },
  },
  MuiTabList: { defaultProps: { centered: true } },
  MuiTabPanel: {
    styleOverrides: {
      root: {
        padding: '12px',
        paddingLeft: '0 !important',
        paddingRight: '0 !important',
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      indicator: ({ theme }) => ({
        backgroundColor: theme.colors.accentColor,
        left: 0,
      }),
      root: {
        '& .MuiTabs-scrollButtons': {
          backgroundColor: themeColors.white,
        },
        paddingLeft: '0 !important',
        paddingRight: '0 !important',
      },
    },
  },
};
